import { gsap } from 'gsap';
import CustomEase from 'gsap/CustomEase';
import { reaction } from 'mobx';
import * as PIXI from 'pixi.js';
import { PixiPlugin } from 'gsap/PixiPlugin';
import { TextPlugin } from 'gsap/TextPlugin';
import { Reel } from '../../core/classes/reel';
import { isoStandard } from '../../core/helpers/isoStandard';
import { initGameLoopManager } from '../../core/helpers/gameLoop';
import { GAME_SPEED_TYPES } from '../../core/constants/gameConstants';
import { addTouchControl } from '../../core/classes/touchSpinControll';
import { amountContainerInfo } from '../../core/constants/amountContainerInfo';
import { handleSymbolAnimationPositionInMatrix } from '../../core/helpers/createWinLinesMatrix';
import { iconStates, specialIconKeys, gameGlobalStates } from '../../core/constants/symbolsNames';

import { gameStore } from '../../store/gameStore';
import { winBarStore } from '../../store/winBarStore';
import { gambleStore } from '../../store/gambleStore';
import { freeBetStore } from '../../store/freeBetStore';
import { userInfoStore } from '../../store/userInfoStore';
import { bettingPartStore } from '../../store/bettingPartStore';

import { gameService } from '../gameService';
import { winPopupShownMinOdd } from '../../gameConstants';
import { SoundTypes, soundService } from '../soundService';
import { gameResourcesService } from '../gameResourcesService';

import { reelsParams, symbolSizes } from '../../core/constants/reelConstants';
import { bonusTypesEnum } from '../../core/constants/bonusTypes';
import { applicationStore } from '../../store/applicationStore';
import { userSettingsStore } from '../../store/userSettingsStore';
import { isStage } from '../../core/helpers/isStage';

class GameDrawer {
  reels = [];

  reelsContainer = null;

  touchSpinScreen = null;

  winContainer = null;

  winLineTimeOutId = null;

  spinDirection = 1;

  randomSymbolGroupId = 0;

  scatter1Count = 0;

  scatter2Count = 0;

  expandingWildsArr = [];

  winAnimations = [];

  initReactions() {
    reaction(
      () => [gameStore.isForceStop, gameStore.isResponseReady],
      ([isForceStop, isResponseReady]) => {
        if (isResponseReady) {
          gameStore.setCanStop(true);
          if (isForceStop) {
            this.reels.forEach((column) => {
              column.setIsForceStop(true);
            });
          }
        }
      }
    );
    reaction(
      () => gameStore.spinResult,
      (spinResult) => {
        if (spinResult) {
          const { additional, matrix } = spinResult;
          if (additional) {
            const { expandingWild } = additional;
            if (expandingWild) {
              this.expandingWildsArr = this.handleExpandingWildArray(expandingWild);
            }
          }
          this.reels.forEach((column) => {
            column.initFromResponseData(matrix);
          });
        }
      }
    );
  }

  setupPixiPlugins() {
    gsap.registerPlugin(PixiPlugin);
    gsap.registerPlugin(TextPlugin);
    gsap.registerPlugin(CustomEase);
    PixiPlugin.registerPIXI(PIXI);
  }

  initReels(ticker) {
    for (let reelIndex = 0; reelIndex < reelsParams.length; reelIndex++) {
      const reel = new Reel({
        index: reelIndex,
        elementId: `reel_${reelIndex}}`,
        reelsContainer: this.reelsContainer,
        slotWinLayer: this.winContainer,
        isLastReel: reelIndex === reelsParams.length - 1,
      });
      this.reels.push(reel);
    }
    this.setupPixiPlugins();
    this.createCustomEases();
    initGameLoopManager(ticker);
    if (!userInfoStore.isReplay && !userSettingsStore.isShowIntro) {
      addTouchControl(this.touchSpinScreen, this.winContainer, this.reels);
    }
  }

  createCustomEases() {
    CustomEase.create(
      'customStopNormal',
      'M0,0 C0,0 0.468,1.064 0.468,1.064 0.468,1.064 0.413,1.064 1,1  '
    );
    CustomEase.create(
      'customStopFast',
      'M0,0 C0,0 0.011,0.028 0.366,1.102 0.741,1.022 0.396,1.074 0.74,1.022 1,1 0.8,1.019 1,1 '
    );
    CustomEase.create(
      'customStopTurbo',
      'M0,0 C0,0 0.422,1 0.443,1.027 0.473,1.064 0.533,1.063 0.533,1.063 0.533,1.063 0.692,1.028 1,1 '
    );
  }

  resetStates() {
    gameStore.setWildsOpened(false);
    gameStore.setCanStop(false);

    gameStore.setIsSpinStart(true);
    if (!gameStore.hasWin) {
      gameStore.setWinEnded(true);
    }
  }

  async startSpinAnimation() {
    await this.resetStates();
    gameResourcesService.resetAllLineSpine();
    await new Promise((resolve) => setTimeout(resolve, gameStore.hasWin ? 100 : 50));
    this.reels.forEach((col) => {
      col.setRotating(true);
      col.setCanRotate(true);
    });
    gameStore.setIsReelsRotating(true);
    gameStore.setSkipWin(false);
  }

  enableNextReelStop(reelIndex) {
    if (reelIndex < 0 || reelIndex >= this.reels.length - 1) {
      console.warn(
        `called enableNextReelStop() for last reel index or invalid index: ${reelIndex}`
      );
      return;
    }
    const isTurbo = bettingPartStore.gameSpeedType === GAME_SPEED_TYPES.turbo;
    if (!isTurbo) {
      this.reels[reelIndex + 1].setCanStop(true);
    }
  }

  spinFromSkip() {
    if (applicationStore.bonusType !== bonusTypesEnum.jackpot) {
      gameStore.setGameGlobalState(gameGlobalStates.none);
      this.spinAction();
    }
  }

  spinAction(isSkipSpinAfterBetChange = false) {
    const { gameGlobalState, isReelsRotating, hasWin } = gameStore;
    switch (gameGlobalState) {
      case gameGlobalStates.show:
        if (hasWin) {
          this.skipWinActions();
        }
        break;
      case gameGlobalStates.waiting:
        if (isReelsRotating) {
          this.forceStopAnimation();
        }
        break;
      case gameGlobalStates.none:
        if (!isSkipSpinAfterBetChange) {
          gameService.doSpin();
        }
        break;
      default:
        break;
    }
  }

  async skipWinActions() {
    gameStore.setSkipWin(true);
    const animationPromises = [];
    this.winAnimations.forEach((winAnimation) => {
      if (winAnimation.icon) {
        winAnimation.icon.goToAnimationEnd(winAnimation.currentState);
      } else {
        if (!gameStore.winEnded) {
          if (!winAnimation?.state?.tracks?.length) return;
          animationPromises.push(
            new Promise((animResolve) => {
              const onComplete = () => {
                winAnimation.removeListener('complete', onComplete, null, 'animation1');
                animResolve();
              };
              winAnimation.addListener('complete', onComplete, null, 'animation1');
            })
          );
        }
        winAnimation.accelerateCurrentAnimation(3);
      }
    });

    const noWildIcons = !this.winAnimations.some(
      (winAnimation) => winAnimation.iconNumber === specialIconKeys.wild
    );
    if (noWildIcons || gameStore.wildsOpened) {
      if (animationPromises) {
        await Promise.all(animationPromises);
      }

      this.spinFromSkip();
    }

    this.winAnimations = [];
  }

  stopSpinAnimation() {
    gameStore.setIsResponseReady(true);
  }

  forceStopAnimation() {
    if (!gameStore.isForceStop) {
      if (isStage()) console.log('FORCESTOP');
      gameStore.setIsForceStop(true);
    }
  }

  resetReelsState() {
    gameStore.setIsSpinStart(false);
    if (gameStore.hasWin) {
      gameStore.setWinEnded(false);
    }
    gameStore.setIsForceStop(false);
    gameStore.setIsReelsRotating(false);
    gameStore.setIsResponseReady(false);

    this.reels.forEach((col) => {
      col.resetReel();
    });
  }

  async setReelsStoped() {
    await this.resetReelsState();
    await this.handleStopReelAnimation();
  }

  handleExpandingWildArray(expandingWild) {
    const expandWildIndexArr = expandingWild.reduce((acc, curr, index) => {
      if (curr) {
        acc.push(index);
      }
      return acc;
    }, []);
    return expandWildIndexArr;
  }

  updateReelsDirection(spinDirection) {
    this.spinDirection = spinDirection;
  }

  resetScatterCount() {
    this.scatter1Count = 0;
    this.scatter2Count = 0;
  }

  async handleStopReelAnimation() {
    const {
      spinResult: { additional = {}, winAmount, gambleInfo, freeBetInfo },
    } = gameStore;
    const { wins, expandingWild } = additional;
    if (!wins?.length) {
      gameStore.setWinEnded(true);
      gameStore.setGameGlobalState(gameGlobalStates.none);
    } else {
      gameStore.setGameGlobalState(gameGlobalStates.show);
    }
    await this.startWinAnimation({
      wins,
      winAmount,
      gambleInfo,
      freeBetInfo,
      expandingWild,
    });
  }

  playWinCoinAnimation() {
    gameResourcesService.winCoinSpine.play('animation');
    gameResourcesService.winCoinSpine.addListener(
      'complete',
      () => {
        gameResourcesService.winCoinSpine.stop();
      },
      null,
      'animation'
    );
  }

  skipWinAnimation() {
    this.winAnimationTimeLine && this.winAnimationTimeLine.progress(1, true);
  }

  animateWinAmount({ value, duration = 0.8 }) {
    if (freeBetStore.currentFreeBet && !value) {
      return;
    }
    const { winAmountTexture } = gameResourcesService;
    gameResourcesService.winAmountTexture.text = '';
    gameResourcesService.winAmountTexture.textContent = '';
    this.winAnimationTimeLine = gsap.timeline();

    this.winAnimationTimeLine.to(winAmountTexture, {
      textContent: value,
      duration,
      ease: 'none',
      snap: { textContent: 0.01 },
      stagger: {
        each: 1.0,
        onUpdate: function () {
          winAmountTexture.text = isoStandard(+this.targets()[0].textContent, true);
        },
      },
      onComplete: function () {
        winAmountTexture.text = isoStandard(+value, true);
      },
    });
  }

  handleScatterWin(iconNumber) {
    this.reels.forEach((column) => {
      column.icons.forEach((icon) => {
        if (icon.iconNumber === iconNumber) {
          icon.changeState(iconStates.win);
        } else {
          icon.changeState(iconStates.noWin);
        }
      });
    });
  }

  async startWinAnimation({
    winAmount,
    gambleInfo,
    freeBetInfo,
    wins = [],
    expandingWild = [0, 0, 0, 0, 0],
  }) {
    if (expandingWild.some((el) => el)) {
      await this.startWildAnimation(expandingWild);
    }
    let winLinesData = [];

    if (winAmount) {
      winBarStore.setWinAmount(winAmount);
      this.animateWinAmount({ value: winAmount });
      winBarStore.updateShowAmountContainerInfo(amountContainerInfo.winContainer, true);
      this.playWinCoinAnimation();
    }
    if (freeBetInfo && !freeBetInfo.reminingSpinCount) {
      winBarStore.setTotalWinAmount(freeBetInfo.totalWin);
      freeBetStore.setCurrentFreeBet({
        ...freeBetStore.currentFreeBet,
        freeBetInfo,
        remainingCount: freeBetInfo.reminingSpinCount,
      });
    }
    gambleStore.setGambleInfo(gambleInfo);
    if (!userInfoStore.isReplay) {
      gameStore.setIsSpinStart(false);
    }
    if (wins.length) {
      winLinesData = handleSymbolAnimationPositionInMatrix(wins);
      await this.startAllWinLineAnimation(winLinesData);
    }
    const scatterWinType = wins.find((el) => !el.line);
    if (scatterWinType) {
      this.handleScatterWin(scatterWinType.symbol);
    }
    gameResourcesService.resetAllLineSpine();
    gameService.spinFinished({
      winAmount,
      gambleInfo,
      odd: gameStore.odd,
    });
    if (!bettingPartStore.selectedAutoBetCount && winLinesData.length) {
      await this.startWinLineAnimation(winLinesData, 0);
    }
  }

  async startWinLineAnimation(winLinesData, start) {
    while (gameStore.gameGlobalState === gameGlobalStates.show) {
      const winLine = winLinesData[start];
      const { line, lines } = winLine;
      const animations = [];
      const lineIndex = line - 1;
      const animationName = 'animation1';
      for (let i = 0; i < this.reels.length; i++) {
        const column = this.reels[i];
        const { visibleRows } = reelsParams[i];
        for (let j = 0; j < visibleRows; j++) {
          const icon = column.icons[j + visibleRows];
          const wildProcessing =
            this.expandingWildsArr.includes(i) && icon.iconNumber === specialIconKeys.wild;
          if (!wildProcessing) {
            animations.push(icon);
            if (lines[j][i] === 1) {
              icon.changeState(iconStates.win);
            } else {
              icon.changeState(iconStates.noWin);
            }
          }
          if (lines[j][i] && line) {
            if (lines[j][i] === 1) {
              const inExpandReel = this.expandingWildsArr.includes(i);
              gameResourcesService.allWinLinesBone[lineIndex][i].y =
                (inExpandReel ? -icon.yPosInWin : -icon.position.y) + reelsParams[i].iconsHeight;
            } else if (lines[j][i] === 2) {
              gameResourcesService.allWinLinesBone[lineIndex][i].y =
                -icon.yPosInWin + reelsParams[i].iconsHeight;
            }
          }
        }
      }
      winBarStore.setWinLine(line);

      if (line) {
        if (gameStore.odd <= winPopupShownMinOdd) {
          // soundService.play(SoundTypes.WinOneLineSound);
        }
        gameResourcesService.allLinesSpine[lineIndex].play(animationName);
        animations.push(gameResourcesService.allLinesSpine[lineIndex]);
        gameResourcesService.allLinesSpine[lineIndex].visible = true;
        await Promise.all(
          animations.map(
            (animation) =>
              new Promise((animResolve) => {
                if (animation?.animationType) {
                  animation.icon.addListener(
                    'complete',
                    () => {
                      animResolve();
                    },
                    null,
                    animation.currentState
                  );
                } else {
                  animation.addListener('complete', () => animResolve(), null, animationName);
                }
              })
          )
        );
      } else {
        await new Promise((resolve) => {
          this.winLineTimeOutId = setTimeout(resolve, 1000);
        });
      }

      start = (start + 1) % winLinesData.length;
    }
  }

  resetExpandingWildsArr() {
    this.expandingWildsArr.length = 0;
  }

  async startAllWinLineAnimation(winLinesData) {
    this.winAnimations = [];
    const animationPromises = [];
    const processedIcons = [];
    const animationName = 'animation1';
    soundService.play(SoundTypes.WinningLineSound);

    winLinesData.forEach(({ line, allLines, lines, count }) => {
      const lineIndex = line - 1;
      if (line) {
        const spineLine = gameResourcesService.allLinesSpine[lineIndex];
        spineLine.visible = true;
        spineLine.play(animationName);
        this.winAnimations.push(spineLine);

        animationPromises.push(
          new Promise((animResolve) => {
            const onComplete = () => {
              spineLine.removeListener('complete', onComplete, null, animationName);
              animResolve();
            };
            spineLine.addListener('complete', onComplete, null, animationName);
          })
        );
      }

      this.reels.forEach((column, i) => {
        const { visibleRows } = reelsParams[i];
        for (let j = 0; j < visibleRows; j++) {
          const icon = column.icons[j + visibleRows];
          const wildProcessing =
            this.expandingWildsArr.includes(i) && icon.iconNumber === specialIconKeys.wild;
          if (!processedIcons.includes(icon) && !wildProcessing) {
            if (allLines[j][i] === 1) {
              this.winAnimations.push(icon);
              processedIcons.push(icon);
              if (icon.iconNumber !== specialIconKeys.wild) {
                icon.changeState(iconStates.win);
              }
            } else if (allLines[j][i] === 2) {
              if (icon.iconNumber === specialIconKeys.wild && count === 2) {
                gameStore.setWildsOpened(true);
                this.winAnimations.push(icon);
              }
              icon.changeState(
                icon.iconNumber === specialIconKeys.wild ? iconStates.idle : iconStates.noWin
              );
              processedIcons.push(icon);
            } else {
              icon.changeState(iconStates.noWin);
            }
          }
          if (lines[j][i] && line) {
            if (lines[j][i] === 1) {
              const inExpandReel = this.expandingWildsArr.includes(i);
              gameResourcesService.allWinLinesBone[lineIndex][i].y =
                (inExpandReel ? -icon.yPosInWin : -icon.position.y) + symbolSizes.height;
            } else if (lines[j][i] === 2) {
              gameResourcesService.allWinLinesBone[lineIndex][i].y =
                -icon.yPosInWin + symbolSizes.height;
            }
          }
        }
      });
    });
    const hasWild = this.winAnimations.some(
      (winAnimation) => winAnimation.iconNumber === specialIconKeys.wild
    );

    if (gameStore.skipWin && hasWild && gameStore.wildsOpened) {
      this.winAnimations.forEach((winAnimation) => {
        if (winAnimation.icon) {
          winAnimation.icon.goToAnimationEnd(winAnimation.currentState);
        } else {
          winAnimation.accelerateCurrentAnimation(3);
        }
      });
    }
    await Promise.all(animationPromises);
    gameStore.setWinEnded(true);

    if (gameStore.skipWin && hasWild && gameStore.wildsOpened) {
      this.spinFromSkip();
    }

    return Promise.resolve();
  }

  startWildAnimation(expandingWild) {
    return new Promise((resolve) => {
      this.expandingWildsArr = this.handleExpandingWildArray(expandingWild);
      gameStore.setIsExpandWildOpen(true);

      this.reels.forEach((reel, reelIndex) => {
        const { visibleRows } = reelsParams[reelIndex];
        if (this.expandingWildsArr.includes(reel.index)) {
          reel.icons.forEach((symbol) => {
            if (symbol.iconNumber === specialIconKeys.wild) {
              let animationKey;
              if (
                symbol.y === reelsParams[reelIndex].iconsFinalPositions.inReel[visibleRows].yInReel
              ) {
                animationKey = 'wild1';
              } else if (
                symbol.y ===
                reelsParams[reelIndex].iconsFinalPositions.inReel[visibleRows + 1].yInReel
              ) {
                animationKey = 'wild2';
              } else if (
                symbol.y ===
                reelsParams[reelIndex].iconsFinalPositions.inReel[visibleRows + 2].yInReel
              ) {
                animationKey = 'wild3';
              } else if (
                symbol.y ===
                reelsParams[reelIndex].iconsFinalPositions.inReel[visibleRows + 3].yInReel
              ) {
                animationKey = 'wild4';
              }

              if (animationKey) {
                soundService.play(SoundTypes.WildOpeningSound);
                symbol.changeState(iconStates.winExpand[animationKey]);
                symbol.isOpen = true;
                this.winAnimations.push(symbol);
                symbol.icon.addListener(
                  'complete',
                  () => {
                    symbol.icon.play(iconStates.idleExpand[animationKey], true);
                    symbol.currentState = iconStates.idleExpand[animationKey];
                    gameStore.setWildsOpened(true);
                    resolve();
                  },
                  null,
                  animationKey
                );
              }
            }
          });
        }
      });
    });
  }

  handleJackpotWin() {
    let resolverCount = 0;
    return new Promise((resolve) => {
      this.reels.forEach((reel) => {
        reel.icons.forEach((slotIcon) => {
          if (slotIcon.iconNumber === 400) {
            slotIcon.icon.play('win', true);
            setTimeout(() => {
              slotIcon.icon.addListener(
                'complete',
                () => {
                  resolverCount += 1;
                  if (resolverCount === 3) {
                    resolve(resolverCount);
                  }
                },
                null,
                'win'
              );
            }, [500]);
          }
        });
      });
    });
  }

  setReelsContainerAndWinContainer(reelsContainer, winContainer, touchSpinScreen) {
    this.reelsContainer = reelsContainer;
    this.winContainer = winContainer;
    this.touchSpinScreen = touchSpinScreen;
  }

  execute(reelsContainer, winContainer, touchSpinScreen, ticker) {
    this.setReelsContainerAndWinContainer(reelsContainer, winContainer, touchSpinScreen);
    this.initReels(ticker);
    this.initReactions();
  }
}

export const gameDrawer = new GameDrawer();
